import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  ASSET_CLASS_PERCENT,
  NotionalLimitConfigurationErrors,
  receiveNotionalLimitSettings,
  Session,
} from 'state';
import {
  NotionalLimitDraftPayload,
  persistNotionalLimitDraft,
} from './notionalLimitDraftApi';

const AUTOSAVE_DELAY_MS = 500;

type DraftSnapshot = {
  selected: number[];
  mode: 0 | 1;
  limits: Record<number, string>;
};

type PendingSnapshot = {
  revision: number;
  draft: DraftSnapshot;
};

export type NotionalLimitDraftSaveStatus =
  | 'loading'
  | 'saved'
  | 'unsaved'
  | 'saving'
  | 'error';

const snapshotFromSession = (session: Session): DraftSnapshot => ({
  selected: [...(session.underlying_assets ?? [])],
  mode: session.instrument_limit_mode ?? ASSET_CLASS_PERCENT,
  limits: Object.fromEntries(
    (session.underlying_asset_notional_limits ?? []).map(row => [
      row.underlying_asset,
      row.notional_limit,
    ]),
  ),
});

const cloneSnapshot = (draft: DraftSnapshot): DraftSnapshot => ({
  selected: [...draft.selected],
  mode: draft.mode,
  limits: { ...draft.limits },
});

const recoveryKey = (sessionId: number) =>
  `pm:notional-limit-draft:${sessionId}`;

const readRecovery = (sessionId: number): DraftSnapshot | null => {
  try {
    const raw = window.sessionStorage.getItem(recoveryKey(sessionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DraftSnapshot>;
    if (
      !Array.isArray(parsed.selected) ||
      !parsed.selected.every(id => Number.isInteger(id)) ||
      (parsed.mode !== 0 && parsed.mode !== 1) ||
      !parsed.limits ||
      typeof parsed.limits !== 'object'
    ) {
      window.sessionStorage.removeItem(recoveryKey(sessionId));
      return null;
    }
    return {
      selected: parsed.selected,
      mode: parsed.mode,
      limits: Object.fromEntries(
        Object.entries(parsed.limits).map(([assetId, value]) => [
          Number(assetId),
          String(value),
        ]),
      ),
    };
  } catch (_error) {
    return null;
  }
};

const writeRecovery = (sessionId: number, draft: DraftSnapshot) => {
  try {
    window.sessionStorage.setItem(
      recoveryKey(sessionId),
      JSON.stringify(draft),
    );
  } catch (_error) {
    // Autosave still works when storage is blocked (for example private mode).
  }
};

const clearRecovery = (sessionId: number) => {
  try {
    window.sessionStorage.removeItem(recoveryKey(sessionId));
  } catch (_error) {
    // Nothing else is required when storage is unavailable.
  }
};

const resolveStateAction = <T>(action: SetStateAction<T>, current: T): T =>
  typeof action === 'function'
    ? (action as (previous: T) => T)(current)
    : action;

export const useNotionalLimitDraft = (
  session: Session,
  minimumHydrationVersion = 0,
) => {
  const dispatch = useDispatch();
  const initial = snapshotFromSession(session);
  const [selected, setSelectedState] = useState(initial.selected);
  const [limitMode, setLimitModeState] = useState<0 | 1>(initial.mode);
  const [notionalLimits, setNotionalLimitsState] = useState(initial.limits);
  const [hydrated, setHydrated] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saveStatus, setSaveStatus] =
    useState<NotionalLimitDraftSaveStatus>('loading');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [launchReady, setLaunchReady] = useState(
    session.notional_limit_launch_ready ?? true,
  );
  const [configurationErrors, setConfigurationErrors] =
    useState<NotionalLimitConfigurationErrors>(
      session.notional_limit_configuration_errors ?? {},
    );

  const sessionIdRef = useRef(session.id);
  const draftRef = useRef<DraftSnapshot>(initial);
  const eligibilityRef = useRef(session.notional_limit_eligibility ?? []);
  const hydratedRef = useRef(false);
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const pendingRef = useRef<PendingSnapshot | null>(null);
  const revisionRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushRef = useRef<() => void>(() => undefined);

  eligibilityRef.current = session.notional_limit_eligibility ?? [];

  const payloadFor = useCallback(
    (draft: DraftSnapshot): NotionalLimitDraftPayload => {
      const eligibleIds = new Set(
        eligibilityRef.current
          .filter(row => row.eligible)
          .map(row => row.underlying_asset),
      );
      return {
        session_id: sessionIdRef.current,
        instrument_limit_mode: draft.mode,
        underlying_assets: [...draft.selected],
        include_sector_alpha_settings: false,
        notional_limits: draft.selected
          .filter(assetId => eligibleIds.has(assetId))
          .filter(assetId => Boolean(draft.limits[assetId]?.trim()))
          .map(assetId => ({
            underlying_asset: assetId,
            notional_limit: draft.limits[assetId].trim(),
          })),
      };
    },
    [],
  );

  const flush = useCallback(() => {
    if (!hydratedRef.current || inFlightRef.current || !pendingRef.current) {
      return;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const pending = pendingRef.current;
    pendingRef.current = null;
    inFlightRef.current = true;
    if (mountedRef.current) {
      setSaveStatus('saving');
      setSaveError(null);
    }

    const payload = payloadFor(pending.draft);
    const requestSessionId = payload.session_id;
    let succeeded = false;
    void persistNotionalLimitDraft(payload)
      .then(result => {
        succeeded = true;
        dispatch(receiveNotionalLimitSettings(result));
        const isLatest =
          requestSessionId === sessionIdRef.current &&
          !pendingRef.current &&
          revisionRef.current === pending.revision;
        if (isLatest) {
          if (result.live_synthetic_bonds_enabled) {
            const normalized = { ...pending.draft, selected: result.underlying_assets, limits: Object.fromEntries(result.notional_limits.map(row => [row.underlying_asset, row.notional_limit])) };
            draftRef.current = normalized;
            setSelectedState(normalized.selected);
            setNotionalLimitsState(normalized.limits);
          }
          clearRecovery(requestSessionId);
          if (mountedRef.current) {
            setDirty(false);
            setSaveStatus('saved');
            setLaunchReady(result.launch_ready);
            setConfigurationErrors(result.configuration_errors);
          }
        }
      })
      .catch(error => {
        if (requestSessionId !== sessionIdRef.current) return;
        // Keep the latest desired snapshot available for an explicit retry. If
        // the trainer edited again while this request ran, that newer snapshot
        // already occupies pendingRef and must win.
        if (!pendingRef.current) pendingRef.current = pending;
        if (mountedRef.current) {
          setDirty(true);
          setSaveStatus('error');
          setSaveError(
            error instanceof Error
              ? error.message
              : 'The instrument-limit draft could not be saved.',
          );
        }
      })
      .finally(() => {
        inFlightRef.current = false;
        if (
          pendingRef.current &&
          mountedRef.current &&
          (succeeded || requestSessionId !== sessionIdRef.current)
        ) {
          flushRef.current();
        }
      });
  }, [dispatch, payloadFor]);
  flushRef.current = flush;

  const schedule = useCallback((draft: DraftSnapshot, immediate: boolean) => {
    const revision = ++revisionRef.current;
    const snapshot = cloneSnapshot(draft);
    pendingRef.current = { revision, draft: snapshot };
    writeRecovery(sessionIdRef.current, snapshot);
    setDirty(true);
    setSaveError(null);
    setSaveStatus(inFlightRef.current ? 'saving' : 'unsaved');
    if (snapshot.mode === 1) setLaunchReady(false);

    if (timerRef.current) clearTimeout(timerRef.current);
    if (immediate) {
      timerRef.current = null;
      flushRef.current();
    } else {
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        flushRef.current();
      }, AUTOSAVE_DELAY_MS);
    }
  }, []);

  const setSelected: Dispatch<SetStateAction<number[]>> = useCallback(
    action => {
      const nextSelected = resolveStateAction(
        action,
        draftRef.current.selected,
      );
      const selectedIds = new Set(nextSelected);
      const next: DraftSnapshot = {
        ...draftRef.current,
        selected: [...nextSelected],
        limits: Object.fromEntries(
          Object.entries(draftRef.current.limits).filter(([assetId]) =>
            selectedIds.has(Number(assetId)),
          ),
        ),
      };
      draftRef.current = next;
      setSelectedState(next.selected);
      setNotionalLimitsState(next.limits);
      schedule(next, true);
    },
    [schedule],
  );

  const setLimitMode: Dispatch<SetStateAction<0 | 1>> = useCallback(
    action => {
      const next: DraftSnapshot = {
        ...draftRef.current,
        mode: resolveStateAction(action, draftRef.current.mode),
      };
      draftRef.current = next;
      setLimitModeState(next.mode);
      schedule(next, true);
    },
    [schedule],
  );

  const setNotionalLimits: Dispatch<SetStateAction<Record<number, string>>> =
    useCallback(
      action => {
        const next: DraftSnapshot = {
          ...draftRef.current,
          limits: resolveStateAction(action, draftRef.current.limits),
        };
        draftRef.current = next;
        setNotionalLimitsState(next.limits);
        schedule(next, false);
      },
      [schedule],
    );

  const flushNow = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    flushRef.current();
  }, []);

  // Reinitialize when navigating directly between sessions without remounting.
  useEffect(() => {
    if (sessionIdRef.current === session.id) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    const next = snapshotFromSession(session);
    sessionIdRef.current = session.id;
    draftRef.current = next;
    pendingRef.current = null;
    revisionRef.current = 0;
    hydratedRef.current = false;
    setSelectedState(next.selected);
    setLimitModeState(next.mode);
    setNotionalLimitsState(next.limits);
    setHydrated(false);
    setDirty(false);
    setSaveStatus('loading');
    setSaveError(null);
    setLaunchReady(session.notional_limit_launch_ready ?? true);
    setConfigurationErrors(session.notional_limit_configuration_errors ?? {});
  }, [session]);

  // The dedicated GET is authoritative even while V1 is active, because the
  // normal V1 serializer intentionally omits dormant V2 rows.
  useEffect(() => {
    if (
      hydratedRef.current ||
      !session.notional_limit_settings_hydrated ||
      (session.notional_limit_settings_hydration_version ?? 0) <
        minimumHydrationVersion ||
      session.id !== sessionIdRef.current
    ) {
      return;
    }
    const persisted = snapshotFromSession(session);
    const recovered = readRecovery(session.id);
    const next = recovered ?? persisted;
    draftRef.current = next;
    setSelectedState(next.selected);
    setLimitModeState(next.mode);
    setNotionalLimitsState(next.limits);
    setLaunchReady(
      recovered ? false : (session.notional_limit_launch_ready ?? true),
    );
    setConfigurationErrors(session.notional_limit_configuration_errors ?? {});
    hydratedRef.current = true;
    setHydrated(true);
    setSaveStatus(recovered ? 'unsaved' : 'saved');
    setDirty(Boolean(recovered));
    if (recovered) schedule(recovered, true);
  }, [minimumHydrationVersion, schedule, session]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!dirty) return undefined;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [dirty]);

  return {
    selected,
    setSelected,
    limitMode,
    setLimitMode,
    notionalLimits,
    setNotionalLimits,
    draftReady: hydrated,
    draftDirty: dirty,
    draftSaving: saveStatus === 'saving',
    draftSaveStatus: saveStatus,
    draftError: saveError,
    draftLaunchReady: launchReady,
    draftConfigurationErrors: configurationErrors,
    flushDraft: flushNow,
  };
};
